import React from 'react'
import { cn } from 'lib/utils'

/** A titled block of the user dialog. */
export const Section = ({
  title,
  action,
  children,
  className,
}: {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
  className?: string
}) => (
  <section className={cn('space-y-2', className)}>
    <div className='flex items-center justify-between gap-2'>
      <h3 className='text-sm font-semibold'>{title}</h3>
      {action}
    </div>
    {children}
  </section>
)

/** A small figure with its label and an optional second line. */
export const InfoTile = ({
  label,
  value,
  hint,
  tone,
}: {
  label: string
  value: React.ReactNode
  hint?: React.ReactNode
  tone?: 'good' | 'warn' | 'bad'
}) => (
  <div
    className={cn(
      'min-w-0 rounded-lg border p-3',
      tone === 'good' && 'border-lime-300 bg-lime-50 dark:bg-lime-500/10',
      tone === 'warn' && 'border-amber-300 bg-amber-50 dark:bg-amber-500/10',
      tone === 'bad' && 'border-red-300 bg-red-50 dark:bg-red-500/10'
    )}
  >
    <div className='text-xs text-muted-foreground'>{label}</div>
    <div className='mt-0.5 truncate font-semibold'>{value}</div>
    {hint && <div className='mt-0.5 text-xs text-muted-foreground'>{hint}</div>}
  </div>
)

/** Label/value pairs, two columns from `sm`. */
export const Fields = ({
  items,
}: {
  items: [label: string, value: React.ReactNode][]
}) => (
  <dl className='grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2'>
    {items.map(([label, value]) => (
      <div key={label} className='flex justify-between gap-3 border-b pb-1'>
        <dt className='text-muted-foreground'>{label}</dt>
        <dd className='min-w-0 text-right break-words'>
          {value === null || value === undefined || value === '' ? '—' : value}
        </dd>
      </div>
    ))}
  </dl>
)
